import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { PageContainer, PageHeader } from '@/components/layout';
import { PageTitle } from '@/components/common/PageTitle';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertCircle,
  Copy,
  Check,
  CreditCard,
  Clock,
  ChevronLeft,
  ChevronRight,
  FileText,
  Printer,
  Settings,
  Trash2,
  Plus,
  Calendar,
  Wifi,
  CreditCard as CardIcon,
} from 'lucide-react';
import { planApi, PaymentRecord, PaymentStatus, PlanCode, CardInfo } from '@/api/planApi';
import { dashboardApi } from '@/api/dashboardApi';
import { workspaceApi } from '@/api/workspaceApi';
import { validateAndBuildOverlay, PaymentOverlayFields } from '@/lib/workspacePaymentOverlay';
import { RefundConfirmDialog } from '@/components/dashboard/plan/RefundConfirmDialog';
import { isPaymentSuperseded } from '@/lib/planUtils';
import { isCardPaymentEnabled, isWorkspaceApiEnabled } from '@/lib/featureFlags';
import { LoadingBlock, Spinner } from '@/components/ui/spinner';

/** workspaceApi 응답이 4초 안에 안 오면 타임아웃으로 간주하고 abort - legacy 그대로 폴백 */
const WORKSPACE_OVERLAY_TIMEOUT_MS = 4000;

// ── 상수 ─────────────────────────────────────────────────────

const BANK_INFO = {
  bank: '국민은행',
  account: '023-21-0059-494',
  holder: '박귀영',
  depositorRule: '가입 아이디 앞 3자리 + 이름 (예: alt홍길동)',
};

// 카드사별 브랜드 색상 및 스타일
const getCardBrandStyle = (cardCompany: string) => {
  const company = cardCompany.toLowerCase();
  if (company.includes('visa')) {
    return {
      gradient: 'from-blue-400 to-blue-600',
      textColor: 'text-white',
      accentColor: 'text-blue-300',
      logoColor: '#1A1F71',
    };
  }
  if (company.includes('master') || company.includes('mastercard')) {
    return {
      gradient: 'from-orange-400 to-orange-600',
      textColor: 'text-white',
      accentColor: 'text-orange-200',
      logoColor: '#EB001B',
    };
  }
  if (company.includes('amex') || company.includes('american')) {
    return {
      gradient: 'from-cyan-500 to-teal-600',
      textColor: 'text-white',
      accentColor: 'text-cyan-200',
      logoColor: '#006FCF',
    };
  }
  if (company.includes('hyundai') || company.includes('현대')) {
    return {
      gradient: 'from-fuchsia-500 to-purple-600',
      textColor: 'text-white',
      accentColor: 'text-fuchsia-200',
      logoColor: '#6B21A8',
    };
  }
  if (company.includes('samsung') || company.includes('삼성')) {
    return {
      gradient: 'from-indigo-400 to-blue-600',
      textColor: 'text-white',
      accentColor: 'text-indigo-200',
      logoColor: '#1428A0',
    };
  }
  if (company.includes('lotte') || company.includes('롯데')) {
    return {
      gradient: 'from-rose-400 to-red-500',
      textColor: 'text-white',
      accentColor: 'text-rose-200',
      logoColor: '#C9252B',
    };
  }
  if (company.includes('shinhan') || company.includes('신한')) {
    return {
      gradient: 'from-blue-400 to-blue-600',
      textColor: 'text-white',
      accentColor: 'text-blue-200',
      logoColor: '#0048BA',
    };
  }
  if (company.includes('kb') || company.includes('국민')) {
    return {
      gradient: 'from-yellow-400 to-orange-500',
      textColor: 'text-white',
      accentColor: 'text-yellow-100',
      logoColor: '#E3A923',
    };
  }
  // 기본값 (검정색 제외)
  return {
    gradient: 'from-slate-300 to-slate-400',
    textColor: 'text-white',
    accentColor: 'text-slate-100',
    logoColor: '#94A3B8',
  };
};

// 카드사 브랜드 마크 표시
const getCardBrandLogo = (cardCompany: string, cardNumber?: string) => {
  // 카드 번호로 브랜드 판별 (우선순위)
  if (cardNumber) {
    const cleanNumber = cardNumber.replace(/\D/g, '');
    if (cleanNumber.startsWith('4')) {
      return (
        <div className="flex items-center gap-1 font-bold text-blue-600">
          <span className="text-xs">VISA</span>
        </div>
      );
    }
    if (cleanNumber.startsWith('5') || cleanNumber.startsWith('2')) {
      return (
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 rounded-full bg-red-500"></div>
          <div className="w-4 h-4 rounded-full bg-yellow-500 -ml-2"></div>
        </div>
      );
    }
    if (cleanNumber.startsWith('34') || cleanNumber.startsWith('37')) {
      return (
        <div className="flex items-center gap-1 font-bold text-blue-600">
          <span className="text-xs">AMEX</span>
        </div>
      );
    }
  }

  // 카드사 이름으로 판별 (대안)
  const company = cardCompany.toLowerCase();
  if (company.includes('visa')) {
    return (
      <div className="flex items-center gap-1 font-bold text-blue-600">
        <span className="text-xs">VISA</span>
      </div>
    );
  }
  if (company.includes('master') || company.includes('mastercard')) {
    return (
      <div className="flex items-center gap-1">
        <div className="w-4 h-4 rounded-full bg-red-500"></div>
        <div className="w-4 h-4 rounded-full bg-yellow-500 -ml-2"></div>
      </div>
    );
  }
  if (company.includes('amex') || company.includes('american')) {
    return (
      <div className="flex items-center gap-1 font-bold text-blue-600">
        <span className="text-xs">AMEX</span>
      </div>
    );
  }
  if (company.includes('hyundai') || company.includes('현대')) {
    return (
      <div className="flex items-center gap-1 font-bold text-purple-600">
        <span className="text-xs">현대</span>
      </div>
    );
  }
  if (company.includes('samsung') || company.includes('삼성')) {
    return (
      <div className="flex items-center gap-1 font-bold text-blue-600">
        <span className="text-xs">삼성</span>
      </div>
    );
  }
  if (company.includes('lotte') || company.includes('롯데')) {
    return (
      <div className="flex items-center gap-1 font-bold text-red-600">
        <span className="text-xs">롯데</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1 font-bold text-gray-600">
      <span className="text-xs">{cardCompany}</span>
    </div>
  );
};

const MERCHANT_INFO = {
  name: '유니버싱정보출판',
  bizNo: '505-06-54982',
  ceo: '임병덕',
  address: '경상북도 경주시 남산동 1035 남산5길 63',
  tel: '054-776-0881',
};

// 입금 기한 계산 함수
// BE PaymentCancellationScheduler.OVERDUE_HOURS(24시간) 초과 시 자동 취소되므로
// 그와 동일하게 24시간 기준으로 계산 (이전에는 7일로 잘못 계산되어 실제
// 취소 시점과 안내 문구가 크게 어긋났었음)
const calculateDepositDeadline = (createdAt: string): { deadline: string; remainingHours: number; isOverdue: boolean } => {
  const created = new Date(createdAt);
  const deadline = new Date(created.getTime() + 24 * 60 * 60 * 1000);
  const now = new Date();
  const remainingMs = deadline.getTime() - now.getTime();
  const remainingHours = Math.ceil(remainingMs / (60 * 60 * 1000));
  return {
    deadline: deadline.toLocaleString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    }),
    remainingHours,
    isOverdue: remainingMs < 0,
  };
};

const PLAN_NAMES: Record<PlanCode, string> = {
  FREE: '무료',
  STARTER: '스타트',
  PROFESSIONAL: '프로',
  ENTERPRISE: '엔터프라이즈',
};

const PLAN_AMOUNTS: Record<PlanCode, number> = {
  FREE: 0,
  STARTER: 9900,
  PROFESSIONAL: 29000,
  ENTERPRISE: 0,
};

const STATUS_MAP: Record<PaymentStatus, { label: string; className: string }> = {
  PENDING: { label: '입금대기', className: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  CONFIRMED: { label: '입금 완료', className: 'bg-green-100 text-green-700 border-green-200' },
  DONE: { label: '결제 완료', className: 'bg-green-100 text-green-700 border-green-200' },
  CANCELLED: { label: '취소', className: 'bg-gray-100 text-gray-500 border-gray-200' },
};

// 증빙(현금영수증/세금계산서) 발행 상태 - 고객에게 보여줄 라벨/색상
const RECEIPT_ISSUE_STATUS_MAP: Record<string, { label: string; className: string }> = {
  NOT_ISSUED: { label: '미발행', className: 'bg-gray-100 text-gray-500 border-gray-200' },
  PENDING: { label: '처리중', className: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  ISSUED: { label: '발행완료', className: 'bg-blue-100 text-blue-700 border-blue-200' },
  FAILED: { label: '발행실패', className: 'bg-red-100 text-red-700 border-red-200' },
  CANCELLED: { label: '취소됨', className: 'bg-gray-100 text-gray-500 border-gray-200' },
};

const PAGE_SIZE = 12;

// ── 컴포넌트 ─────────────────────────────────────────────────

export default function BillingPage() {
  const [records, setRecords] = useState<PaymentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptRecord, setReceiptRecord] = useState<PaymentRecord | null>(null);
  const [issuingReceiptId, setIssuingReceiptId] = useState<number | null>(null);
  const [receiptActionError, setReceiptActionError] = useState<string | null>(null);
  const [refundTarget, setRefundTarget] = useState<PaymentRecord | null>(null);
  const [myTeamRole, setMyTeamRole] = useState<'OWNER' | 'ADMIN' | 'MEMBER' | null>(null);
  const [activeTab, setActiveTab] = useState('bank');
  const [cards, setCards] = useState<CardInfo[]>([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const [cardDeleteConfirm, setCardDeleteConfirm] = useState<{ open: boolean; cardId: number | null }>({ open: false, cardId: null });
  const [deletingCard, setDeletingCard] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState<{ startDate: string; endDate: string; nextBillingDate: string } | null>(null);
  const [cardRegisterOpen, setCardRegisterOpen] = useState(false);
  const [registeringCard, setRegisteringCard] = useState(false);
  const [cardForm, setCardForm] = useState({
    cardNumber: '',
    cardExpiration: '',  // MM/YY 형식
    customerIdentityNumber: '',
    cardPassword: '',
    cardOwnerName: '',
    isDefault: true,
  });
  const [cardFormError, setCardFormError] = useState<string | null>(null);

  // workspaceApi.getMyBilling()로 status/amount/paymentMethod/billingMonth/createdAt/
  // confirmedAt/plan/영수증·환불 표시 필드만 덮어쓰는 overlay. records(legacy)가 행
  // 존재/순서/id/teamId/contractId의 유일한 진실 소스로 계속 유지되며, 이 overlay는
  // 성공+검증 통과 시에만 표시 필드 일부를 보강한다 - 실패(에러/타임아웃/부분 응답
  // 이상) 시엔 전부 legacy 그대로 렌더링되므로 화면이 멈추는 경로가 없다.
  const [workspaceOverlay, setWorkspaceOverlay] = useState<Record<number, PaymentOverlayFields>>({});

  useEffect(() => {
    if (!isWorkspaceApiEnabled) {
      setWorkspaceOverlay({});
      return;
    }
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), WORKSPACE_OVERLAY_TIMEOUT_MS);

    workspaceApi
      .getMyBilling({ page: currentPage, size: PAGE_SIZE }, { signal: controller.signal })
      .then((res) => {
        const overlay = validateAndBuildOverlay(res.data?.content, records);
        setWorkspaceOverlay(overlay);
        if (Object.keys(overlay).length === 0 && records.length > 0) {
          console.warn('[workspace-api] invalid/mismatched billing response, falling back to legacy display');
        }
      })
      .catch((err) => {
        console.warn('[workspace-api] getMyBilling failed/timed out, falling back to legacy display', err);
        setWorkspaceOverlay({});
      })
      .finally(() => clearTimeout(timeoutId));

    return () => {
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, [records, currentPage]);

  const displayRecords = useMemo<PaymentRecord[]>(
    () => records.map((r) => ({ ...r, ...workspaceOverlay[r.id] })),
    [records, workspaceOverlay]
  );

  // 입금 대기 중인 건 (최근)
  const pendingRecord = displayRecords.find(r => r.status === 'PENDING');

  const load = useCallback(async (page: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await planApi.getBillingHistory({ page, size: PAGE_SIZE });
      const records = res.data.content ?? [];
      // createdAt 기준 내림차순 정렬 (최신 결제가 위로)
      records.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
      setRecords(records);
      setTotalPages(res.data.totalPages ?? 0);
      setTotalElements(res.data.totalElements ?? 0);
      setCurrentPage(res.data.number ?? page);
    } catch {
      setError('결제 이력을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCards = useCallback(async () => {
    setCardsLoading(true);
    setCardError(null);
    try {
      const res = await planApi.getCards();
      const cardList = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
      setCards(cardList);
    } catch {
      setCardError('카드 목록을 불러오지 못했습니다.');
    } finally {
      setCardsLoading(false);
    }
  }, []);

  const loadBillingPeriod = useCallback(async () => {
    try {
      const period = await dashboardApi.getBillingPeriod();
      setBillingPeriod(period);
    } catch {
      // 청구 기간 로드 실패는 치명적이지 않으므로 무시
    }
  }, []);

  // 현재 로그인한 사용자의 팀 내 역할 (환불 버튼을 팀장에게만 보여주기 위함).
  // PaymentRecord.teamRole은 "그 결제를 만든 팀 오너"의 역할이라 항상 OWNER로
  // 고정돼있어 현재 조회자의 권한 판단에 쓸 수 없음 - 별도로 조회해야 함.
  const loadMyTeamRole = useCallback(async () => {
    try {
      const status = await dashboardApi.getAiUsageStatus();
      setMyTeamRole(status.isTeamContext ? status.role : null);
    } catch {
      setMyTeamRole(null);
    }
  }, []);

  useEffect(() => { load(0); }, [load]);
  useEffect(() => { loadCards(); }, [loadCards]);
  useEffect(() => { loadBillingPeriod(); }, [loadBillingPeriod]);
  useEffect(() => { loadMyTeamRole(); }, [loadMyTeamRole]);

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      // 클립보드 실패 시 무시
    }
  };

  const handleRefund = (payment: PaymentRecord) => setRefundTarget(payment);

  const handleRefunded = async ({ message, teamStatus }: { message: string; teamStatus?: string | null }) => {
    let fullMessage = message;
    if (teamStatus === 'INACTIVE') {
      fullMessage += '\n\n팀이 비활성화되었습니다. 팀원들은 더 이상 서비스를 이용할 수 없습니다.';
    }
    alert(fullMessage);
    setRefundTarget(null);
    load(currentPage);
  };

  // 대체된(superseded) 결제 건인지 확인 - src/lib/planUtils.ts의 isPaymentSuperseded 참고
  // (PlanPage.tsx와 공용 - 각자 구현하다 어긋나지 않도록 통일함)
  const isSuperseded = (payment: PaymentRecord) => isPaymentSuperseded(payment, records);

  const canRefund = (payment: PaymentRecord | null) => {
    if (!payment) return false;

    // 결제일로부터 7일 이내인지 확인
    const paymentDate = new Date(payment.createdAt);
    const daysSincePayment = (Date.now() - paymentDate.getTime()) / (1000 * 60 * 60 * 24);
    const within7Days = daysSincePayment <= 7;

    // 결제 상태가 CANCELLED가 아닌지 확인
    const notCancelled = payment.status !== 'CANCELLED';

    // 결제 상태가 PENDING(입금 대기)가 아닌지 확인
    const notPending = payment.status !== 'PENDING';

    // 팀 결제인 경우 팀장만 환불 가능 (myTeamRole = 현재 로그인한 사용자의 팀 내 역할)
    if (payment.teamId && myTeamRole !== 'OWNER') return false;

    // 유료 플랜 결제만 환불 가능
    const isPaidPlan = payment.amount > 0;

    // 이미 상위 계약/플랜으로 대체된 예전 결제 건은 환불 대상에서 제외
    // (백엔드도 최신 건만 환불을 허용하도록 동일하게 검증함)
    const notSuperseded = !isSuperseded(payment);

    return within7Days && notCancelled && notPending && isPaidPlan && notSuperseded;
  };

  const handleDeleteCard = (cardId: number) => {
    setCardDeleteConfirm({ open: true, cardId });
  };

  const handleConfirmDeleteCard = async () => {
    if (!cardDeleteConfirm.cardId) return;
    setDeletingCard(true);
    try {
      await planApi.deleteCard(cardDeleteConfirm.cardId);
      setCardDeleteConfirm({ open: false, cardId: null });
      loadCards();
    } catch (err: any) {
      setCardError(err?.response?.data?.message ?? '카드 삭제 중 오류가 발생했습니다.');
    } finally {
      setDeletingCard(false);
    }
  };

  const handleRegisterCard = async () => {
    // 기본 검증
    if (!cardForm.cardNumber || !cardForm.cardExpiration ||
        !cardForm.customerIdentityNumber || !cardForm.cardPassword || !cardForm.cardOwnerName) {
      setCardFormError('모든 필드를 입력해주세요.');
      return;
    }
    // 유효기간 MM/YY 검사
    const expiryClean = cardForm.cardExpiration.replace(/\D/g, '');
    if (expiryClean.length !== 4) {
      setCardFormError('유효기간을 MM/YY 형식으로 입력하세요 (예: 11/27).');
      return;
    }
    const month = expiryClean.slice(0, 2);
    const year = expiryClean.slice(2, 4);
    const monthNum = parseInt(month, 10);
    if (monthNum < 1 || monthNum > 12) {
      setCardFormError('유효한 월(01-12)을 입력하세요.');
      return;
    }

    setRegisteringCard(true);
    setCardFormError(null);
    try {
      // customerKey는 현재 사용자 ID로 설정 (실제로는 사용자 정보에서 가져와야 함)
      const customerKey = 'current_user'; // 임시값, 실제 사용자 ID로 교체 필요
      // 연도 2자리를 4자리로 변환 (YY → 20YY)
      const fullYear = `20${year}`;
      await planApi.registerCard({
        customerKey,
        cardNumber: cardForm.cardNumber,
        cardExpirationYear: fullYear,
        cardExpirationMonth: month,
        customerIdentityNumber: cardForm.customerIdentityNumber,
        cardPassword: cardForm.cardPassword,
        cardOwnerName: cardForm.cardOwnerName,
        isDefault: cardForm.isDefault,
      });
      setCardRegisterOpen(false);
      setCardForm({
        cardNumber: '',
        cardExpiration: '',
        customerIdentityNumber: '',
        cardPassword: '',
        cardOwnerName: '',
        isDefault: true,
      });
      loadCards();
    } catch (err: any) {
      setCardFormError(err?.response?.data?.message ?? '카드 등록 중 오류가 발생했습니다.');
    } finally {
      setRegisteringCard(false);
    }
  };

  const formatDate = (iso: string) =>
    iso ? new Date(iso).toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' }) : '-';

  const formatDateTime = (iso: string) =>
    iso ? new Date(iso).toLocaleString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    }) : '-';

  const formatCardExpiration = (value: string) => {
    const v = value.replace(/\D/g, '').slice(0, 4);
    if (v.length >= 3) {
      return `${v.slice(0, 2)}/${v.slice(2)}`;
    }
    return v;
  };

  const formatAmount = (amount: number) => `₩${amount.toLocaleString()}`;

  const isReceiptIssued = (record: PaymentRecord) =>
    record.cashReceiptIssued === true || Boolean(record.cashReceiptIssuedAt) || Boolean(record.cashReceiptNo);

  const openReceipt = (record: PaymentRecord) => {
    setReceiptActionError(null);
    setReceiptRecord(record);
    setReceiptOpen(true);
  };

  const handleIssueReceipt = async () => {
    if (!receiptRecord) return;
    setIssuingReceiptId(receiptRecord.id);
    setReceiptActionError(null);

    try {
      const res = await planApi.issueCashReceipt(receiptRecord.id);
      const updated: PaymentRecord = {
        ...receiptRecord,
        cashReceiptNo: res.data.cashReceiptNo,
        cashReceiptIssuedAt: res.data.cashReceiptIssuedAt,
        cashReceiptIssued: true,
        receiptType: 'CASH_RECEIPT',
        receiptIssueStatus: 'ISSUED',
        receiptIssueNo: res.data.cashReceiptNo,
      };
      setReceiptRecord(updated);
      setRecords((prev) => prev.map((item) => (item.id === updated.id ? { ...item, ...updated } : item)));
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? '현금영수증 발급 처리 중 오류가 발생했습니다.';
      setReceiptActionError(msg);
    } finally {
      setIssuingReceiptId(null);
    }
  };

  const handlePrintReceipt = () => {
    if (!receiptRecord || !isReceiptIssued(receiptRecord)) return;

    const issuedAt = receiptRecord.cashReceiptIssuedAt ?? '';
    const issuedAtLabel = issuedAt ? new Date(issuedAt).toLocaleString('ko-KR') : '-';
    const receiptNo = receiptRecord.cashReceiptNo ?? '-';
    const amount = receiptRecord.amount;
    const supply = Math.floor(amount / 1.1);
    const vat = amount - supply;
    const printWindow = window.open('', 'cash-receipt-print', 'width=860,height=960');

    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <title>현금영수증</title>
          <style>
            body { font-family: sans-serif; margin: 24px; color: #111827; }
            .title { font-size: 20px; font-weight: 700; margin-bottom: 12px; }
            .sub { font-size: 12px; color: #6b7280; margin-bottom: 16px; }
            .box { border: 1px solid #e5e7eb; border-radius: 8px; margin-bottom: 12px; }
            .box h3 { margin: 0; padding: 10px 12px; font-size: 13px; background: #f9fafb; border-bottom: 1px solid #e5e7eb; }
            .row { display: flex; gap: 12px; padding: 8px 12px; font-size: 13px; }
            .label { width: 120px; color: #6b7280; }
            .value { flex: 1; text-align: left; }
            .total { font-weight: 700; }
          </style>
        </head>
        <body>
          <div class="title">현금영수증 (거래확인용)</div>
          <div class="sub">승인번호: ${receiptNo} / 발행일시: ${issuedAtLabel}</div>

          <div class="box">
            <h3>가맹점 정보</h3>
            <div class="row"><div class="label">상호</div><div class="value">${MERCHANT_INFO.name}</div></div>
            <div class="row"><div class="label">사업자번호</div><div class="value">${MERCHANT_INFO.bizNo}</div></div>
            <div class="row"><div class="label">대표자</div><div class="value">${MERCHANT_INFO.ceo}</div></div>
            <div class="row"><div class="label">연락처</div><div class="value">${MERCHANT_INFO.tel}</div></div>
            <div class="row"><div class="label">주소</div><div class="value">${MERCHANT_INFO.address}</div></div>
          </div>

          <div class="box">
            <h3>결제 정보</h3>
            <div class="row"><div class="label">결제월</div><div class="value">${receiptRecord.billingMonth}</div></div>
            <div class="row"><div class="label">플랜</div><div class="value">${PLAN_NAMES[receiptRecord.plan]}</div></div>
            <div class="row"><div class="label">결제수단</div><div class="value">계좌이체</div></div>
            <div class="row"><div class="label">입금자명</div><div class="value">${receiptRecord.depositorName ?? '-'}</div></div>
          </div>

          <div class="box">
            <h3>금액 상세</h3>
            <div class="row"><div class="label">공급가액</div><div class="value">₩${supply.toLocaleString()}</div></div>
            <div class="row"><div class="label">부가세</div><div class="value">₩${vat.toLocaleString()}</div></div>
            <div class="row total"><div class="label">합계</div><div class="value">₩${amount.toLocaleString()}</div></div>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const receiptNo = receiptRecord?.cashReceiptNo ?? '-';
  const receiptIssuedAt = receiptRecord?.cashReceiptIssuedAt ?? '';
  const receiptIssued = receiptRecord ? isReceiptIssued(receiptRecord) : false;
  const receiptAmount = receiptRecord?.amount ?? 0;
  const supplyAmount = Math.floor(receiptAmount / 1.1);
  const vatAmount = receiptAmount - supplyAmount;
  const receiptType = receiptRecord?.receiptType;
  const isCashReceiptIssued = receiptType === 'CASH_RECEIPT' && receiptIssued;
  // 세금계산서는 바로빌 자동발행이 비활성 상태라 관리자가 승인번호를 직접
  // 입력해 ISSUED로 바꾸기 전까지는 "발행 완료"가 아니라 처리 대기 상태다.
  const isTaxInvoicePending = receiptType === 'TAX_INVOICE' && receiptRecord?.receiptIssueStatus !== 'ISSUED';
  const isTaxInvoiceIssued = receiptType === 'TAX_INVOICE' && receiptRecord?.receiptIssueStatus === 'ISSUED';
  const hasReceiptSetting = receiptType && receiptType !== 'NONE';
  const isTransactionConfirmation = !receiptType || receiptType === 'NONE';

  return (
    <>
      <PageTitle title="결제 관리" />
      <PageContainer maxWidth="full">
      <PageHeader
        title="결제 안내"
        description="계좌이체 및 카드 결제 안내 및 결제 이력을 확인할 수 있습니다."
      />

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* 증빙 신청 설정 바로가기 */}
      <Link to="/dashboard/my/receipt-info" className="block">
        <Card className="border-dashed border-primary/40 hover:border-primary hover:bg-primary/5 transition-colors cursor-pointer">
          <CardContent className="py-3 px-4 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <Settings className="w-4 h-4 text-primary" />
              <div>
                <p className="text-sm font-medium text-gray-800">증빙 신청 설정</p>
                <p className="text-xs text-gray-500">현금영수증 또는 세금계산서 자동 발행 설정</p>
              </div>
            </div>
            <span className="text-xs text-primary font-medium">설정하기 →</span>
          </CardContent>
        </Card>
      </Link>

      {/* 입금 대기 중 알림 */}
      {pendingRecord && (() => {
        // 다운그레이드 예약 건은 청구주기 종료일까지 PENDING 유지가 정상 설계라
        // 업그레이드용 "24시간 입금 기한" 안내를 적용하면 안 됨(0원이면 애초에
        // 입금할 금액도 없음). 백엔드도 이제 이런 건은 24시간 자동취소 대상에서
        // 제외하므로, 프론트도 그와 일치하는 안내만 보여준다.
        if (pendingRecord.isDowngrade) {
          return (
            <Alert className="border-blue-300 bg-blue-50">
              <Clock className="w-4 h-4" />
              <AlertDescription className="text-blue-700">
                <strong>{PLAN_NAMES[pendingRecord.plan]} 플랜 ({formatAmount(pendingRecord.amount)})</strong>
                {pendingRecord.amount === 0 ? (
                  <> 다운그레이드가 예약되어 있습니다. 별도로 입금하실 금액은 없으며, 예약된 날짜에 자동으로 전환됩니다.</>
                ) : (
                  <> 다운그레이드가 예약되어 있습니다. 예약일 전까지 아래 계좌로 입금해 주시면 관리자 확인 후 반영됩니다. (24시간 기한이 적용되지 않으며 자동 취소되지 않습니다)</>
                )}
              </AlertDescription>
            </Alert>
          );
        }

        const { deadline, remainingHours, isOverdue } = calculateDepositDeadline(pendingRecord.createdAt);
        return (
          <Alert className={isOverdue ? "border-red-300 bg-red-50" : "border-yellow-300 bg-yellow-50"}>
            <Clock className="w-4 h-4" />
            <AlertDescription className={isOverdue ? "text-red-700" : "text-yellow-700"}>
              <strong>{PLAN_NAMES[pendingRecord.plan]} 플랜 ({formatAmount(pendingRecord.amount)})</strong>
              {isOverdue ? (
                // 아직 status는 PENDING(실제 취소는 매시간 배치가 처리)이므로
                // "이미 취소됐다"고 단정하지 않고 "곧 취소된다"로 정확하게 안내
                <> 입금 기한(24시간)이 경과했습니다. 입금이 확인되지 않으면 곧 자동 취소됩니다.</>
              ) : (
                <> 입금 대기 중입니다. 입금 기한: {deadline}까지 ({remainingHours}시간 남음, 미입금 시 자동 취소됩니다)</>
              )}
              {!isOverdue && <> 아래 계좌로 납부해 주시면 영업일 기준 1일 이내에 처리됩니다.</>}
            </AlertDescription>
          </Alert>
        );
      })()}

      {/* 이용 기간 */}
      {billingPeriod && (
        <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-4 h-4 text-gray-600" />
            <span className="font-semibold text-gray-800">이용 기간</span>
          </div>
          <div className="text-xs text-gray-700 space-y-1">
            <p>{new Date(billingPeriod.startDate).toLocaleDateString('ko-KR')} ~ {new Date(billingPeriod.endDate).toLocaleDateString('ko-KR')}</p>
            <p>다음 결제일: {new Date(billingPeriod.nextBillingDate).toLocaleDateString('ko-KR')}</p>
          </div>
        </div>
      )}

      {/* 결제 수단 안내 섹션 */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <CreditCard className="w-4 h-4 text-primary" />
            결제 수단 안내
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="bank">계좌이체</TabsTrigger>
              <TabsTrigger value="card">카드 결제</TabsTrigger>
            </TabsList>
            <TabsContent value="bank" className="space-y-4 mt-4">
              {/* 입금 계좌 정보 */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                <p className="text-sm font-semibold text-blue-800 mb-2">입금 계좌 정보</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                  <InfoRow label="은행" value={BANK_INFO.bank} />
                  <InfoRow
                    label="계좌번호"
                    value={BANK_INFO.account}
                    copyable
                    copied={copiedField === 'account'}
                    onCopy={() => handleCopy(BANK_INFO.account, 'account')}
                  />
                  <InfoRow label="예금주" value={BANK_INFO.holder} />
                </div>
              </div>

              {/* 안내 사항 */}
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-start gap-2">
                  <span className="text-primary font-bold mt-0.5">•</span>
                  <div>
                    <p className="font-medium text-gray-700">입금자명 규칙</p>
                    <p className="text-gray-500">{BANK_INFO.depositorRule}</p>
                    <div className="mt-1 bg-gray-100 rounded px-3 py-1.5 inline-flex items-center gap-2">
                      <code className="text-xs text-gray-700">{BANK_INFO.depositorRule}</code>
                      <button
                        onClick={() => handleCopy(BANK_INFO.depositorRule, 'rule')}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        {copiedField === 'rule' ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-primary font-bold mt-0.5">•</span>
                  <p>입금 후 처리 소요 시간: <strong>영업일 기준 1일 이내</strong></p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-primary font-bold mt-0.5">•</span>
                  <p>
                    문의: <a href="/dashboard/inquiry" className="text-primary underline">1:1문의</a>
                  </p>
                </div>
              </div>
            </TabsContent>
            <TabsContent value="card" className="space-y-4 mt-4">
              {cardError && (
                <Alert variant="destructive">
                  <AlertCircle className="w-4 h-4" />
                  <AlertDescription>{cardError}</AlertDescription>
                </Alert>
              )}

              {/* 카드 결제 안내 가이드라인 */}
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-start gap-2">
                  <span className="text-primary font-bold mt-0.5">•</span>
                  <p>카드 결제는 매월 자동으로 결제됩니다.</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-primary font-bold mt-0.5">•</span>
                  <p>결제일 3일 전부터는 카드 변경/삭제가 제한됩니다.</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-primary font-bold mt-0.5">•</span>
                  <p>결제 실패 시 서비스 이용이 제한될 수 있습니다.</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-primary font-bold mt-0.5">•</span>
                  <p>카드 변경 시 즉시 새 카드로 결제됩니다.</p>
                </div>
              </div>

              {/* 등록된 카드 목록 */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <h3 className="text-sm font-semibold text-gray-900">등록된 카드 ({cards.length})</h3>
                  {isCardPaymentEnabled ? (
                    <Button variant="outline" size="sm" className="gap-2" onClick={() => setCardRegisterOpen(true)}>
                      <Plus className="w-4 h-4" />
                      카드 추가
                    </Button>
                  ) : (
                    <Badge className="border border-gray-300 bg-gray-50 text-gray-500">곧 서비스 예정</Badge>
                  )}
                </div>
                {cardsLoading ? (
                  <LoadingBlock size="lg" py={6} />
                ) : cards.length === 0 ? (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                    <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    {isCardPaymentEnabled ? (
                      <>
                        <p className="text-sm text-gray-600 mb-3">카드가 등록되지 않았습니다.</p>
                        <Button variant="outline" size="sm" className="gap-2" onClick={() => setCardRegisterOpen(true)}>
                          <Plus className="w-4 h-4" />
                          카드 등록하기
                        </Button>
                      </>
                    ) : (
                      <p className="text-sm text-gray-600">카드 결제는 곧 서비스될 예정입니다. 계좌이체를 이용해 주세요.</p>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {cards.map((card) => {
                      const brandStyle = getCardBrandStyle(card.cardCompany);
                      return (
                        <div
                          key={card.id}
                          className={`relative bg-gradient-to-br ${brandStyle.gradient} rounded-xl p-4 text-white shadow-lg overflow-hidden max-w-sm`}
                        >
                          {/* 상단: 카드사 브랜드 마크와 기본 뱃지 */}
                          <div className="flex justify-between items-start mb-4">
                            <div className="flex items-center gap-3">
                              {getCardBrandLogo(card.cardCompany, card.cardNumberMasked)}
                              <div>
                                <div className="font-semibold text-white">{card.cardNumberMasked}</div>
                                <div className="text-sm text-white/80">
                                  Expires {card.cardExpirationYear}/{card.cardExpirationMonth}
                                </div>
                              </div>
                            </div>
                            {card.isDefault && (
                              <Badge className="bg-white/20 text-white border-white/30 text-xs backdrop-blur-sm">
                                Default
                              </Badge>
                            )}
                          </div>

                          {/* 하단: 삭제 버튼 */}
                          <div className="flex justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-white hover:text-white hover:bg-white/20 h-8 px-2"
                              onClick={() => handleDeleteCard(card.id)}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* 문의 */}
              <div className="flex items-start gap-2">
                <span className="text-primary font-bold mt-0.5">•</span>
                <p>
                  문의: <a href="/dashboard/inquiry" className="text-primary underline">1:1문의</a>
                </p>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* 결제 이력 */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">결제 이력</CardTitle>
            <span className="text-xs text-gray-400">최근 12개월 기준 (총 {totalElements.toLocaleString()}건)</span>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <LoadingBlock size="lg" py={10} />
          ) : records.length === 0 ? (
            <p className="text-center text-gray-400 py-10 text-sm">결제 이력이 없습니다.</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>결제월</TableHead>
                    <TableHead>생성일</TableHead>
                    <TableHead>플랜</TableHead>
                    <TableHead className="text-right">금액</TableHead>
                    <TableHead>결제방식</TableHead>
                    <TableHead>상태</TableHead>
                    <TableHead>처리일</TableHead>
                    <TableHead className="text-right">영수증</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayRecords.map(record => {
                    // 0원 다운그레이드 예약 건은 입금할 금액이 없으므로 "입금대기"로 표시하면 안 됨
                    const isFreeDowngradeReservation = record.status === 'PENDING' && record.isDowngrade && record.amount === 0;
                    const isRefunded = record.status === 'CANCELLED' && !!record.refundAmount;
                    // 계좌이체 환불은 관리자가 실제 송금완료 처리하기 전까지 refundCompletedAt이
                    // null로 남음 - 그동안은 "환불 완료"가 아니라 "환불 접수(처리중)"로 표시해야
                    // 실제 송금이 안 됐는데 완료된 것처럼 보이는 문제가 없다. 카드는 Toss가
                    // 동기 승인하므로 refundCompletedAt이 즉시 채워져 바로 "환불 완료"로 표시됨.
                    const isRefundPending = isRefunded && record.paymentMethod === 'BANK_TRANSFER' && !record.refundCompletedAt;
                    const superseded = isSuperseded(record);
                    const status = isFreeDowngradeReservation
                      ? { label: '다운그레이드 예약중', className: 'bg-blue-100 text-blue-700 border-blue-200' }
                      : isRefundPending
                      ? { label: '환불 접수(처리중)', className: 'bg-orange-50 text-orange-600 border-orange-200' }
                      : isRefunded
                      ? { label: '환불 완료', className: 'bg-red-50 text-red-600 border-red-200' }
                      : superseded
                      ? { label: '대체됨', className: 'bg-gray-100 text-gray-500 border-gray-200' }
                      : STATUS_MAP[record.status] || { label: record.status, className: 'bg-gray-100 text-gray-500 border-gray-200' };
                    const canIssueReceipt = record.status === 'CONFIRMED' && !superseded && record.paymentMethod === 'BANK_TRANSFER' && record.receiptType === 'CASH_RECEIPT';
                    const issuedReceipt = isReceiptIssued(record);
                    const isEnterprise = record.plan === 'ENTERPRISE';
                    const showReceiptButton = record.amount > 0 && (canIssueReceipt || issuedReceipt);
                    const receiptType = record.receiptType;
                    const isTaxInvoice = receiptType === 'TAX_INVOICE';
                    const isCashReceipt = receiptType === 'CASH_RECEIPT';
                    const hasReceiptSetting = receiptType && receiptType !== 'NONE';
                    return (
                      <TableRow key={record.id}>
                        <TableCell className="font-medium">{record.billingMonth}</TableCell>
                        <TableCell className="text-gray-500 text-xs">{formatDateTime(record.createdAt)}</TableCell>
                        <TableCell>
                          <div>
                            <div>{PLAN_NAMES[record.plan]}</div>
                            {isEnterprise && record.contractName && (
                              <div className="text-xs text-gray-500">{record.contractName}</div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div>
                            <div>{formatAmount(record.amount)}</div>
                            {isEnterprise && record.monthlyLimit && (
                              <div className="text-xs text-gray-500">{record.monthlyLimit.toLocaleString()}건</div>
                            )}
                            {isRefunded && (
                              <div className="text-xs text-red-500">환불: {formatAmount(record.refundAmount!)}</div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-gray-500">
                          {record.amount === 0 ? '' : (record.paymentMethod === 'CARD' ? '카드' : '계좌이체')}
                        </TableCell>
                        <TableCell>
                          <Badge className={`text-xs border ${status.className}`}>{status.label}</Badge>
                        </TableCell>
                        <TableCell className="text-gray-500 text-xs">
                          {isRefunded && record.refundedAt
                            ? isRefundPending
                              ? `환불 접수: ${formatDate(record.refundedAt)}`
                              : `환불: ${formatDate(record.refundedAt)}${record.refundCompletedAt ? ` (완료: ${formatDate(record.refundCompletedAt)})` : ''}`
                            : record.confirmedAt ? formatDate(record.confirmedAt) : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex flex-col items-end gap-1">
                            {hasReceiptSetting && record.receiptIssueStatus && RECEIPT_ISSUE_STATUS_MAP[record.receiptIssueStatus] && (
                              <Badge className={`text-[11px] border w-fit ${RECEIPT_ISSUE_STATUS_MAP[record.receiptIssueStatus].className}`}>
                                {RECEIPT_ISSUE_STATUS_MAP[record.receiptIssueStatus].label}
                              </Badge>
                            )}
                            <div className="flex gap-1 justify-end">
                            {/* 결제/다운그레이드 예약 취소는 /dashboard/my/plan에서 처리 (여기서는 중복이라 제거함) */}
                            {canRefund(record) && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50"
                                onClick={() => handleRefund(record)}
                              >
                                환불
                              </Button>
                            )}
                            {showReceiptButton && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs"
                                disabled={!canIssueReceipt && !issuedReceipt}
                                title={
                                  issuedReceipt
                                    ? '현금영수증 보기'
                                    : canIssueReceipt
                                    ? '현금영수증 발급'
                                    : superseded
                                    ? '이미 다른 플랜/계약으로 대체된 결제 건입니다.'
                                    : '확인완료 상태에서만 발급할 수 있습니다.'
                                }
                                onClick={() => openReceipt(record)}
                              >
                                {issuedReceipt ? '보기' : '발급'}
                              </Button>
                            )}
                            {isTaxInvoice && record.amount > 0 && !superseded && record.status === 'CONFIRMED' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs"
                                title={record.receiptIssueStatus === 'ISSUED' ? '세금계산서 보기' : '세금계산서 발행 상태 확인'}
                                onClick={() => openReceipt(record)}
                              >
                                {record.receiptIssueStatus === 'ISSUED' ? '보기' : '확인'}
                              </Button>
                            )}
                            {!hasReceiptSetting && record.amount > 0 && !superseded && record.status === 'CONFIRMED' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs"
                                title="거래 확인서 보기"
                                onClick={() => openReceipt(record)}
                              >
                                거래확인서
                              </Button>
                            )}
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              </div>

              {/* 페이지네이션 */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 py-4 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === 0}
                    onClick={() => load(currentPage - 1)}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-gray-500">{currentPage + 1} / {totalPages}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage >= totalPages - 1}
                    onClick={() => load(currentPage + 1)}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* 현금영수증 모달 */}
      <Dialog open={receiptOpen} onOpenChange={setReceiptOpen}>
        <DialogContent className="max-w-2xl p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              {isCashReceiptIssued ? '현금영수증'
                : receiptType === 'CASH_RECEIPT' ? '현금영수증 발급'
                : isTaxInvoiceIssued ? '세금계산서'
                : isTaxInvoicePending ? '세금계산서 발행 대기'
                : '거래 확인서'}
            </DialogTitle>
            <DialogDescription>
              {isCashReceiptIssued ? '국세청에 등록된 현금영수증입니다.'
                : receiptType === 'CASH_RECEIPT' ? '국세청에 실제로 등록되는 현금영수증입니다.'
                : isTaxInvoiceIssued ? '국세청에 등록된 세금계산서입니다.'
                : isTaxInvoicePending ? '관리자 처리 후 실제 세금계산서로 등록됩니다.'
                : '결제 내역 확인용 영수증입니다.'}
            </DialogDescription>
          </DialogHeader>

          {receiptRecord && (
            <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
              {isCashReceiptIssued ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div className="rounded-md border p-3">
                      <p className="text-xs text-gray-500 mb-1">승인번호</p>
                      <p className="font-semibold text-gray-900">{receiptNo}</p>
                    </div>
                    <div className="rounded-md border p-3">
                      <p className="text-xs text-gray-500 mb-1">발행일시</p>
                      <p className="font-semibold text-gray-900">{formatDateTime(receiptIssuedAt)}</p>
                    </div>
                  </div>

                  <div className="rounded-md border overflow-hidden">
                    <div className="bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-700">가맹점 정보</div>
                    <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <ReceiptRow label="상호" value={MERCHANT_INFO.name} />
                      <ReceiptRow label="사업자번호" value={MERCHANT_INFO.bizNo} />
                      <ReceiptRow label="대표자" value={MERCHANT_INFO.ceo} />
                      <ReceiptRow label="연락처" value={MERCHANT_INFO.tel} />
                      <div className="sm:col-span-2">
                        <ReceiptRow label="주소" value={MERCHANT_INFO.address} />
                      </div>
                    </div>
                  </div>

                  <div className="rounded-md border overflow-hidden">
                    <div className="bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-700">결제 정보</div>
                    <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <ReceiptRow label="결제월" value={receiptRecord.billingMonth} />
                      <ReceiptRow label="플랜" value={PLAN_NAMES[receiptRecord.plan]} />
                      <ReceiptRow label="결제수단" value="계좌이체" />
                      <ReceiptRow label="입금자명" value={receiptRecord.depositorName ?? '-'} />
                      <ReceiptRow label="결제금액" value={formatAmount(receiptAmount)} />
                      <ReceiptRow label="상태" value={STATUS_MAP[receiptRecord.status].label} />
                    </div>
                  </div>

                  <div className="rounded-md border overflow-hidden">
                    <div className="bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-700">금액 상세</div>
                    <div className="p-3 space-y-1.5 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">공급가액</span>
                        <span className="font-medium">{formatAmount(supplyAmount)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">부가세</span>
                        <span className="font-medium">{formatAmount(vatAmount)}</span>
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t">
                        <span className="font-semibold text-gray-900">합계</span>
                        <span className="font-bold text-gray-900">{formatAmount(receiptAmount)}</span>
                      </div>
                    </div>
                  </div>

                  <p className="text-xs text-gray-400 text-center">
                    본 영수증은 국세청에 등록된 현금영수증입니다.
                  </p>

                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setReceiptOpen(false)}>닫기</Button>
                    <Button onClick={handlePrintReceipt}>
                      <Printer className="w-4 h-4 mr-2" />
                      인쇄
                    </Button>
                  </div>
                </>
              ) : receiptType === 'CASH_RECEIPT' ? (
                <>
                  <Alert className="border-blue-200 bg-blue-50">
                    <AlertCircle className="w-4 h-4 text-blue-600" />
                    <AlertDescription className="text-blue-700">
                      아직 현금영수증이 발급되지 않았습니다. 발급 버튼을 누르면 국세청에 실제로 등록됩니다.
                    </AlertDescription>
                  </Alert>

                  {receiptActionError && (
                    <Alert variant="destructive">
                      <AlertCircle className="w-4 h-4" />
                      <AlertDescription>{receiptActionError}</AlertDescription>
                    </Alert>
                  )}

                  <div className="rounded-md border overflow-hidden">
                    <div className="bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-700">결제 정보</div>
                    <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <ReceiptRow label="결제월" value={receiptRecord.billingMonth} />
                      <ReceiptRow label="플랜" value={PLAN_NAMES[receiptRecord.plan]} />
                      <ReceiptRow label="결제수단" value="계좌이체" />
                      <ReceiptRow label="입금자명" value={receiptRecord.depositorName ?? '-'} />
                      <ReceiptRow label="결제금액" value={formatAmount(receiptAmount)} />
                      <ReceiptRow label="상태" value={STATUS_MAP[receiptRecord.status].label} />
                    </div>
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setReceiptOpen(false)}>닫기</Button>
                    <Button onClick={handleIssueReceipt} disabled={issuingReceiptId === receiptRecord.id}>
                      {issuingReceiptId === receiptRecord.id && <Spinner size="sm" className="mr-2" />}
                      발급 처리
                    </Button>
                  </div>
                </>
              ) : isTaxInvoiceIssued ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div className="rounded-md border p-3">
                      <p className="text-xs text-gray-500 mb-1">승인번호</p>
                      <p className="font-semibold text-gray-900">{receiptRecord.receiptIssueNo ?? '-'}</p>
                    </div>
                    <div className="rounded-md border p-3">
                      <p className="text-xs text-gray-500 mb-1">발행일시</p>
                      <p className="font-semibold text-gray-900">{formatDateTime(receiptRecord.receiptIssuedAt ?? '')}</p>
                    </div>
                  </div>

                  <div className="rounded-md border overflow-hidden">
                    <div className="bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-700">결제 정보</div>
                    <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <ReceiptRow label="결제월" value={receiptRecord.billingMonth} />
                      <ReceiptRow label="플랜" value={PLAN_NAMES[receiptRecord.plan]} />
                      <ReceiptRow label="결제수단" value="계좌이체" />
                      <ReceiptRow label="입금자명" value={receiptRecord.depositorName ?? '-'} />
                      <ReceiptRow label="결제금액" value={formatAmount(receiptAmount)} />
                      <ReceiptRow label="상태" value={STATUS_MAP[receiptRecord.status].label} />
                    </div>
                  </div>

                  <div className="rounded-md border overflow-hidden">
                    <div className="bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-700">금액 상세</div>
                    <div className="p-3 space-y-1.5 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">공급가액</span>
                        <span className="font-medium">{formatAmount(supplyAmount)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">부가세</span>
                        <span className="font-medium">{formatAmount(vatAmount)}</span>
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t">
                        <span className="font-semibold text-gray-900">합계</span>
                        <span className="font-bold text-gray-900">{formatAmount(receiptAmount)}</span>
                      </div>
                    </div>
                  </div>

                  <p className="text-xs text-gray-400 text-center">
                    본 영수증은 국세청에 등록된 세금계산서입니다.
                  </p>

                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setReceiptOpen(false)}>닫기</Button>
                  </div>
                </>
              ) : isTaxInvoicePending ? (
                <>
                  <Alert className="border-yellow-200 bg-yellow-50">
                    <AlertCircle className="w-4 h-4 text-yellow-600" />
                    <AlertDescription className="text-yellow-700">
                      세금계산서는 관리자가 국세청 홈택스에서 직접 발행합니다. 발행 완료 후 이메일로 전송됩니다.
                    </AlertDescription>
                  </Alert>

                  <div className="rounded-md border overflow-hidden">
                    <div className="bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-700">결제 정보</div>
                    <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <ReceiptRow label="결제월" value={receiptRecord.billingMonth} />
                      <ReceiptRow label="플랜" value={PLAN_NAMES[receiptRecord.plan]} />
                      <ReceiptRow label="결제수단" value="계좌이체" />
                      <ReceiptRow label="입금자명" value={receiptRecord.depositorName ?? '-'} />
                      <ReceiptRow label="결제금액" value={formatAmount(receiptAmount)} />
                      <ReceiptRow label="상태" value={STATUS_MAP[receiptRecord.status].label} />
                    </div>
                  </div>

                  <div className="rounded-md border overflow-hidden">
                    <div className="bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-700">금액 상세</div>
                    <div className="p-3 space-y-1.5 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">공급가액</span>
                        <span className="font-medium">{formatAmount(supplyAmount)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">부가세</span>
                        <span className="font-medium">{formatAmount(vatAmount)}</span>
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t">
                        <span className="font-semibold text-gray-900">합계</span>
                        <span className="font-bold text-gray-900">{formatAmount(receiptAmount)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setReceiptOpen(false)}>닫기</Button>
                  </div>
                </>
              ) : (
                <>
                  <Alert className="border-gray-200 bg-gray-50">
                    <AlertCircle className="w-4 h-4 text-gray-600" />
                    <AlertDescription className="text-gray-700">
                      거래 확인용 영수증입니다. 증빙 신청 설정에서 현금영수증 또는 세금계산서를 설정하면 자동 발행됩니다.
                    </AlertDescription>
                  </Alert>

                  <div className="rounded-md border overflow-hidden">
                    <div className="bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-700">결제 정보</div>
                    <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <ReceiptRow label="결제월" value={receiptRecord.billingMonth} />
                      <ReceiptRow label="플랜" value={PLAN_NAMES[receiptRecord.plan]} />
                      <ReceiptRow label="결제수단" value={receiptRecord.paymentMethod === 'CARD' ? '카드' : '계좌이체'} />
                      <ReceiptRow label="입금자명" value={receiptRecord.depositorName ?? '-'} />
                      <ReceiptRow label="결제금액" value={formatAmount(receiptAmount)} />
                      <ReceiptRow label="상태" value={STATUS_MAP[receiptRecord.status].label} />
                    </div>
                  </div>

                  <div className="rounded-md border overflow-hidden">
                    <div className="bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-700">금액 상세</div>
                    <div className="p-3 space-y-1.5 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">공급가액</span>
                        <span className="font-medium">{formatAmount(supplyAmount)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">부가세</span>
                        <span className="font-medium">{formatAmount(vatAmount)}</span>
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t">
                        <span className="font-semibold text-gray-900">합계</span>
                        <span className="font-bold text-gray-900">{formatAmount(receiptAmount)}</span>
                      </div>
                    </div>
                  </div>

                  <p className="text-xs text-gray-400 text-center">
                    본 영수증은 거래 확인용으로 발행되었습니다.
                  </p>

                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setReceiptOpen(false)}>닫기</Button>
                    <Button onClick={handlePrintReceipt}>
                      <Printer className="w-4 h-4 mr-2" />
                      인쇄
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* 환불 확인 Dialog (BillingPage/PlanPage 공용) */}
      <RefundConfirmDialog
        payment={refundTarget}
        onClose={() => setRefundTarget(null)}
        onRefunded={handleRefunded}
      />

      {/* 카드 삭제 확인 Dialog */}
      <Dialog open={cardDeleteConfirm.open} onOpenChange={(open) => !deletingCard && setCardDeleteConfirm({ open, cardId: null })}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>카드 삭제</DialogTitle>
          </DialogHeader>
          <DialogDescription>
            카드를 삭제하시겠습니까? 카드를 삭제하면 다음 결제일에 결제가 실패할 수 있습니다.
          </DialogDescription>
          {cardError && (
            <Alert variant="destructive" className="mt-3">
              <AlertCircle className="w-4 h-4" />
              <AlertDescription>{cardError}</AlertDescription>
            </Alert>
          )}
          <div className="flex gap-3 justify-end mt-4">
            <Button variant="outline" onClick={() => setCardDeleteConfirm({ open: false, cardId: null })} disabled={deletingCard}>
              취소
            </Button>
            <Button onClick={handleConfirmDeleteCard} disabled={deletingCard}>
              {deletingCard ? <Spinner size="sm" className="mr-2" /> : null}
              확인
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* 카드 등록 Dialog */}
      <Dialog open={cardRegisterOpen} onOpenChange={(open) => !registeringCard && setCardRegisterOpen(open)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>카드 등록</DialogTitle>
            <DialogDescription>
              결제에 사용할 카드 정보를 입력해주세요.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-xs text-gray-500">
              국내에서 발급된 카드만 정기결제(자동결제)가 가능합니다. 해외 카드(마스터카드, VISA 등)는 등록이 제한됩니다.
            </p>
            {cardFormError && (
              <Alert variant="destructive">
                <AlertCircle className="w-4 h-4" />
                <AlertDescription>{cardFormError}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="cardNumber">카드 번호</Label>
              <Input
                id="cardNumber"
                placeholder="0000-0000-0000-0000"
                value={cardForm.cardNumber}
                onChange={(e) => setCardForm({ ...cardForm, cardNumber: e.target.value })}
                disabled={registeringCard}
                autoComplete="off"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cardExpiration">유효기간</Label>
              <Input
                id="cardExpiration"
                placeholder="MM/YY"
                value={formatCardExpiration(cardForm.cardExpiration)}
                onChange={(e) => setCardForm({ ...cardForm, cardExpiration: e.target.value.replace(/\D/g, '') })}
                disabled={registeringCard}
                maxLength={5}
                autoComplete="off"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="customerIdentityNumber">생년월일 (YYMMDD) 또는 사업자번호</Label>
              <Input
                id="customerIdentityNumber"
                name="customerIdentityNumber"
                placeholder="900101 또는 10자리 사업자번호"
                value={cardForm.customerIdentityNumber}
                onChange={(e) => setCardForm({ ...cardForm, customerIdentityNumber: e.target.value })}
                disabled={registeringCard}
                autoComplete="new-password"
                defaultValue=""
                readOnly
                onFocus={(e) => e.target.readOnly = false}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cardPassword">카드 비밀번호 (앞 2자리)</Label>
              <Input
                id="cardPassword"
                type="password"
                placeholder="**"
                maxLength={2}
                value={cardForm.cardPassword}
                onChange={(e) => setCardForm({ ...cardForm, cardPassword: e.target.value })}
                disabled={registeringCard}
                autoComplete="off"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cardOwnerName">카드 소유자명</Label>
              <Input
                id="cardOwnerName"
                placeholder="홍길동"
                value={cardForm.cardOwnerName}
                onChange={(e) => setCardForm({ ...cardForm, cardOwnerName: e.target.value })}
                disabled={registeringCard}
                autoComplete="off"
              />
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => setCardRegisterOpen(false)} disabled={registeringCard}>
              취소
            </Button>
            <Button onClick={handleRegisterCard} disabled={registeringCard}>
              {registeringCard ? <Spinner size="sm" className="mr-2" /> : null}
              등록
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </PageContainer>
    </>
  );
}

// ── 서브 컴포넌트 ─────────────────────────────────────────────

interface InfoRowProps {
  label: string;
  value: string;
  copyable?: boolean;
  copied?: boolean;
  onCopy?: () => void;
}

function InfoRow({ label, value, copyable, copied, onCopy }: InfoRowProps) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-blue-600 font-medium">{label}</span>
      <div className="flex items-center gap-1.5">
        <span className="font-semibold text-gray-800">{value}</span>
        {copyable && (
          <button onClick={onCopy} className="text-blue-400 hover:text-blue-600">
            {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        )}
      </div>
    </div>
  );
}

interface ReceiptRowProps {
  label: string;
  value: string;
}

function ReceiptRow({ label, value }: ReceiptRowProps) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-gray-900 text-left break-all">{value}</span>
    </div>
  );
}
